/**
 * Gas limits, and the zero fee every console transaction carries.
 *
 * The network's gas price is 0uddt and its gas meter stays (ADR-003). So a
 * transaction still declares a gas limit, and still fails if it runs out, but
 * nothing is charged for the gas it declares: `feeFor` returns an empty fee.
 * Everything else in this file is about getting the LIMIT right, and every
 * limit is biased high because the two ways to be wrong are not symmetric. A
 * limit that is too high is never charged for. A limit that is too low runs out
 * of gas mid-execution: the transaction fails, nothing it was meant to create
 * exists, the tenant has spent a wallet approval for nothing, and the error
 * names a gas limit rather than anything they did.
 *
 * Kept free of any CosmJS *runtime* import, because this module is in the
 * initial bundle: the deposit checks and the screens that build a transaction
 * import it long before anything is signed. CosmJS is 1.4 MB — nine times the
 * rest of the application — and a tenant browsing providers is not signing
 * anything, so it is loaded on demand at the first signature instead. See
 * tx.ts.
 *
 * `import type` is erased at build time (`verbatimModuleSyntax` guarantees it),
 * so the StdFee type below costs nothing.
 */

import type { StdFee } from '@cosmjs/stargate'

/**
 * Gas limit for a bank send.
 *
 * A constant rather than a simulation, and only defensible because MsgSend's cost
 * does not vary: the same handful of store writes every time. Pre-signature
 * simulation needs a transaction assembled with a placeholder signature — Cosmos
 * skips signature verification when simulating — and that is worth building for
 * the deployment flow, where an SDL-bearing message ranges from a few kilobytes to
 * tens of them and a constant would be a guess. Simulating *after* signing, which
 * `/api/tenant/tx/simulate` supports, would cost the user a second approval of the
 * same transaction.
 */
export const GAS_SEND = 100_000

/**
 * Gas for `MsgCreateDeployment`.
 *
 * Unlike `GAS_SEND` this is **a heuristic, not a defensible constant**, and the
 * comment above says why: the message carries the group spec, so its cost scales
 * with how much SDL the tenant wrote — one service with one placement is a fraction
 * of the work of eight services across three placements. A single number would be a
 * guess for every deployment except the one it was measured on.
 *
 * So it is derived from the encoded size, and deliberately biased high, because the
 * two failure directions are not symmetric. Too much gas is never charged for: the
 * limit is a ceiling, and the gas price is zero. Too little runs out of gas
 * *mid-execution*: the transaction fails, the deployment is not created, and the
 * error names a gas limit rather than anything the tenant did.
 *
 * The real fix is pre-signature simulation — build the transaction with a
 * placeholder signature, ask the chain what it costs, then sign the real one. The
 * chain skips signature verification when simulating, so this is possible and is
 * simply not built yet; `/api/tenant/tx/simulate` exists but simulating *after*
 * signing would cost the user a second wallet approval of the same transaction.
 * Until then, this is an upper bound with its reasoning stated rather than a number
 * with none.
 */
export const GAS_CREATE_DEPLOYMENT_BASE = 400_000
export const GAS_CREATE_DEPLOYMENT_PER_BYTE = 200
export const GAS_CREATE_DEPLOYMENT_MAX = 10_000_000

export function gasForCreateDeployment(encodedBytes: number): number {
  const gas = GAS_CREATE_DEPLOYMENT_BASE + encodedBytes * GAS_CREATE_DEPLOYMENT_PER_BYTE
  return Math.min(gas, GAS_CREATE_DEPLOYMENT_MAX)
}

/**
 * Gas for `MsgCreateLease` — choosing an offer.
 *
 * This was a flat 250,000, and a tenant ran out of gas at 251,622: the
 * transaction failed, no lease was created, and Retry resent the same limit, so
 * it failed the same way every time.
 *
 * A flat number cannot be right, because the handler's work is not fixed. It
 * closes every losing bid and refunds that provider's deposit — an escrow
 * account close, a bank transfer and several store writes per offer — so the
 * cost grows with how many offers the order received. That part is visible
 * here: it is the offer list the tenant is choosing from.
 *
 * It also walks every registered provider to record missed bids for the
 * reputation module, so the cost grows with the size of the network too. That
 * part is NOT visible to the console, and no heuristic over the offer count
 * covers it; the base is set well above the observed failure to absorb it for
 * now. The real fix is the same as for the deployment heuristic above:
 * simulate before signing.
 *
 * Reasoned, not measured, and biased high for the same asymmetry as the rest
 * of this file: too much gas is never charged for, too little fails and
 * creates nothing.
 */
export const GAS_CREATE_LEASE_BASE = 400_000
export const GAS_CREATE_LEASE_PER_OFFER = 60_000
export const GAS_CREATE_LEASE_MAX = 5_000_000

export function gasForCreateLease(offerCount: number): number {
  const offers = Math.max(1, Math.floor(offerCount))
  return Math.min(GAS_CREATE_LEASE_BASE + offers * GAS_CREATE_LEASE_PER_OFFER, GAS_CREATE_LEASE_MAX)
}

/**
 * Gas for `MsgCloseDeployment`, by the number of leases it will close.
 *
 * This was a flat 180,000, and every close of a deployment with a running lease
 * ran out of gas: closing one settles the lease's escrow, refunds the rest and
 * deletes the lease records, and that roughly doubles the work. Measured on the
 * lab chain (2026-09-27): closes with no lease used 97,852–114,436 gas, and the
 * close of 91957, with one active lease, used 210,727. The out-of-gas receipts
 * before it said 180,203, 180,900 and 210,361, which are where each limit ran
 * out, not what the close needed. Both terms keep at least half again over the
 * measurement, for the reason in the header.
 */
export const GAS_CLOSE_DEPLOYMENT_BASE = 200_000
export const GAS_CLOSE_DEPLOYMENT_PER_LEASE = 200_000
export const GAS_CLOSE_DEPLOYMENT_MAX = 5_000_000

export function gasForCloseDeployment(activeLeases: number): number {
  const leases = Number.isFinite(activeLeases) ? Math.max(0, Math.floor(activeLeases)) : 0
  return Math.min(GAS_CLOSE_DEPLOYMENT_BASE + leases * GAS_CLOSE_DEPLOYMENT_PER_LEASE, GAS_CLOSE_DEPLOYMENT_MAX)
}

/**
 * Gas for `MsgAccountDeposit` (escrow top-up).
 *
 * The message is small and fixed-size — a signer, an escrow account id and one
 * coin — and the handler does a deposit authorization, one bank transfer into
 * the escrow module and an account write. Set at the same order as the close
 * paths' old 180k and biased up, for the reason in the header: too little gas is a
 * failed transaction; too much is never charged for. Not measured against a live
 * chain.
 */
export const GAS_ACCOUNT_DEPOSIT = 200_000

/**
 * Gas for `MsgUpdateDeployment` (Update instance).
 *
 * Fixed-size too: a deployment id and a 32-byte hash. The handler reads the
 * deployment, compares the hash and writes it back with one event — no loop
 * over groups, leases or offers (pkg.akt.dev/node@v1.1.1
 * x/deployment/handler/server.go:95-120). Set with the deposit and close paths
 * and biased up for the same reason. Not measured against a live chain.
 */
export const GAS_UPDATE_DEPLOYMENT = 200_000

/**
 * The fee for a transaction with this gas limit: nothing.
 *
 * gas × 0uddt is zero (ADR-003), and a zero fee is an EMPTY amount rather than
 * a zero coin: that is what a zero gas price produces, and what the CLI and the
 * issuer sign. Only the gas limit is carried, because the chain still meters
 * gas and still fails a transaction that runs out.
 *
 * Building an empty fee is half of it; the wallet must also sign this one
 * rather than an estimate of its own. tx.ts asks Keplr and Leap for
 * `preferNoSetFee`, and wallet.ts suggests the chain with zero gas-price steps.
 */
export function feeFor(gas: number): StdFee {
  return { amount: [], gas: String(gas) }
}

/** Whole tokens as typed by a user → the micro denomination the chain takes. */
export function toMicroAmount(input: string): string | null {
  const n = Number(input)
  if (!Number.isFinite(n) || n <= 0) return null
  return BigInt(Math.round(n * 1_000_000)).toString()
}

/**
 * Gas for `MsgAcceptDirectQuote`.
 *
 * A constant, and defensible as one in a way `gasForCreateDeployment` is not:
 * the message carries a fixed-size quote — addresses, a 32-byte hash, a
 * 32-byte nonce, a 64-byte signature and a handful of scalars — so unlike an
 * SDL-bearing deployment its encoded size barely moves.
 *
 * The work it does on chain is not small, though: a secp256k1 verification,
 * the full market bid admission path (deposit, bid cap, attribute and
 * capability match, the reputation gate, escrow authorization), then lease
 * creation with its escrow payment and the closure of losing bids. So this is
 * biased high for the same asymmetric reason as the deployment heuristic —
 * too much gas is never charged for, too little runs out mid-execution and
 * leaves no lease.
 *
 * It is measured against nothing yet. Gate 6's lab run is where a real number
 * comes from, and until then this is an upper bound with its reasoning stated.
 *
 * TODO(three-path-rental, lab): replace this reasoned bound with a measured
 * one. The measurement is the canonical Gate 6 run's **actual gas used by
 * `MsgCreateDeployment + MsgAcceptDirectQuote`** — the `gas_used` field of the
 * accepted direct-rental transaction's delivery result, taken from the single
 * immutable raw log the evidence checker is run against, not from a separate
 * simulation. It needs a live lab (a real provider quote, a real signature to
 * verify, a real market admission path), so it is the integration owner's step
 * rather than something this repository can do.
 *
 * When it lands: subtract the same run's `MsgCreateDeployment`-only gas to get
 * the quote's own share, apply a stated safety margin over it, and record both
 * the measured figure and the margin in `fees.test.ts` — the regression there
 * pins this constant precisely so the swap is a visible, argued diff rather
 * than a number that changed. The margin should be generous (≥1.5×) for the
 * asymmetry described above: too much gas is never charged for, and too little
 * fails, creates nothing, and reports a gas limit rather than anything they
 * did.
 */
export const GAS_ACCEPT_DIRECT_QUOTE = 900_000

/**
 * Gas for the whole two-message direct rental.
 *
 * Summed rather than maxed, because both messages execute in the same
 * transaction and each consumes its own gas — taking the larger of the two
 * would under-fund the pair by exactly the smaller one, and the failure would
 * arrive mid-execution after the deployment had already been created and
 * charged for.
 */
export function gasForDirectRental(deploymentEncodedBytes: number): number {
  return Math.min(
    gasForCreateDeployment(deploymentEncodedBytes) + GAS_ACCEPT_DIRECT_QUOTE,
    GAS_CREATE_DEPLOYMENT_MAX + GAS_ACCEPT_DIRECT_QUOTE,
  )
}
